import { create } from 'zustand';
import { useAuthStore } from './authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export interface JiraIntegration {
  id: string;
  domain: string;
  email: string;
  has_token: boolean;
  project_key: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface JiraFormData {
  domain: string;
  email: string;
  api_token?: string;
  project_key: string;
}

export interface JiraProject {
  id: string;
  key: string;
  name: string;
}

export interface JiraBoard {
  id: number;
  name: string;
}

export interface JiraSprint {
  id: number;
  name: string;
  state: string;
  start_date?: string;
  end_date?: string;
}

export interface JiraIssue {
  id: string;
  key: string;
  summary: string;
  status: {
    name: string;
    category: string; // 'new' | 'indeterminate' | 'done'
  };
  assignee: {
    display_name: string;
    avatar?: string;
  } | null;
  priority: string;
  issue_type: string;
  url: string;
}

export interface SprintReport {
  sprint_id: number;
  sprint_name: string;
  total: number;
  done_count: number;
  in_progress_count: number;
  todo_count: number;
  completion_rate: number;
  assignees: { name: string; total: number; done: number; avatar?: string }[];
  issue_types: { name: string; count: number }[];
  carry_over: JiraIssue[];
}

export interface VelocitySprintData {
  id: number;
  name: string;
  state: string;
  total: number;
  done: number;
}

export interface VelocityData {
  sprints: VelocitySprintData[];
}

export interface BurnupDay {
  day: number;
  date: string;
  ideal: number;
  actual: number;
}

export interface BurnupData {
  days: BurnupDay[];
  total: number;
  sprint: { id: number; name: string; state: string; start_date?: string; end_date?: string };
}

export interface DeploymentFreqDay {
  date: string;
  closed: number;
}

export interface DeploymentFrequencyData {
  days: DeploymentFreqDay[];
  total_closed: number;
  sprint: { id: number; name: string; state: string };
}

interface JiraState {
  integration: JiraIntegration | null;
  projects: JiraProject[];
  boards: JiraBoard[];
  selectedProjectKey: string | null;
  sprints: JiraSprint[];
  boardId: number | null;
  issues: JiraIssue[];
  selectedSprintId: number | null;
  sprintReport: SprintReport | null;
  velocityData: VelocityData | null;
  burnupData: BurnupData | null;
  deploymentFrequencyData: DeploymentFrequencyData | null;
  loadingIntegration: boolean;
  loadingProjects: boolean;
  loadingBoards: boolean;
  loadingSprints: boolean;
  loadingIssues: boolean;
  loadingReport: boolean;
  loadingVelocity: boolean;
  loadingBurnup: boolean;
  loadingDeploymentFrequency: boolean;
  error: string | null;

  fetchIntegration: (teamId: string) => Promise<void>;
  saveIntegration: (teamId: string, data: JiraFormData) => Promise<void>;
  deleteIntegration: (teamId: string) => Promise<void>;
  testConnection: (teamId: string) => Promise<{ success: boolean; message: string }>;
  fetchProjects: (teamId: string) => Promise<void>;
  fetchBoards: (teamId: string, projectKey: string) => Promise<void>;
  selectProject: (teamId: string, projectKey: string) => void;
  selectBoard: (teamId: string, boardId: number) => void;
  fetchSprints: (teamId: string, boardId?: number) => Promise<void>;
  fetchIssues: (teamId: string, sprintId?: number) => Promise<void>;
  fetchSprintReport: (teamId: string, sprintId: number, sprintName?: string) => Promise<void>;
  fetchVelocity: (teamId: string, boardId: number) => Promise<void>;
  fetchBurnup: (teamId: string, sprintId: number) => Promise<void>;
  fetchDeploymentFrequency: (teamId: string, sprintId: number) => Promise<void>;
  selectSprint: (teamId: string, sprintId: number | null) => void;
  reset: () => void;
}

function authHeaders() {
  const token = useAuthStore.getState().tokens?.access;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse(res: Response) {
  if (res.status === 204) return null;
  const data = await res.json();
  if (!res.ok) {
    const msg = data?.error || data?.detail || 'Something went wrong';
    throw new Error(msg);
  }
  return data;
}

export const useJiraStore = create<JiraState>((set, get) => ({
  integration: null,
  projects: [],
  boards: [],
  selectedProjectKey: null,
  sprints: [],
  boardId: null,
  issues: [],
  selectedSprintId: null,
  sprintReport: null,
  velocityData: null,
  burnupData: null,
  deploymentFrequencyData: null,
  loadingIntegration: false,
  loadingProjects: false,
  loadingBoards: false,
  loadingSprints: false,
  loadingIssues: false,
  loadingReport: false,
  loadingVelocity: false,
  loadingBurnup: false,
  loadingDeploymentFrequency: false,
  error: null,

  fetchIntegration: async (teamId) => {
    set({ loadingIntegration: true, error: null });
    try {
      const res = await fetch(`${API_BASE_URL}/teams/${teamId}/jira/`, { headers: authHeaders() });
      const data = await handleResponse(res);
      set({ integration: data, loadingIntegration: false });
    } catch (e: any) {
      set({ error: e.message, loadingIntegration: false });
    }
  },

  saveIntegration: async (teamId, data) => {
    const res = await fetch(`${API_BASE_URL}/teams/${teamId}/jira/`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    const result = await handleResponse(res);
    set({ integration: result });
  },

  deleteIntegration: async (teamId) => {
    const res = await fetch(`${API_BASE_URL}/teams/${teamId}/jira/`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data?.error || 'Failed to disconnect');
    }
    set({
      integration: null,
      projects: [], boards: [], selectedProjectKey: null,
      sprints: [], issues: [], selectedSprintId: null, boardId: null,
      velocityData: null,
    });
  },

  testConnection: async (teamId) => {
    const res = await fetch(`${API_BASE_URL}/teams/${teamId}/jira/test/`, {
      method: 'POST',
      headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok) {
      return { success: false, message: data?.error || 'Connection failed' };
    }
    return { success: true, message: `Connected as ${data.user?.display_name || data.user?.email}` };
  },

  fetchProjects: async (teamId) => {
    set({ loadingProjects: true, error: null });
    try {
      const res = await fetch(`${API_BASE_URL}/teams/${teamId}/jira/projects/`, { headers: authHeaders() });
      const data = await handleResponse(res);
      set({ projects: data || [], loadingProjects: false });
    } catch (e: any) {
      set({ error: e.message, loadingProjects: false });
    }
  },

  fetchBoards: async (teamId, projectKey) => {
    set({ loadingBoards: true, boards: [], boardId: null, error: null });
    try {
      const res = await fetch(
        `${API_BASE_URL}/teams/${teamId}/jira/boards/?project_key=${encodeURIComponent(projectKey)}`,
        { headers: authHeaders() },
      );
      const data = await handleResponse(res);
      const boards: JiraBoard[] = data || [];
      set({ boards, loadingBoards: false });
      if (boards.length > 0) {
        get().selectBoard(teamId, boards[0].id);
      }
    } catch (e: any) {
      set({ error: e.message, loadingBoards: false });
    }
  },

  selectProject: (teamId, projectKey) => {
    set({
      selectedProjectKey: projectKey,
      boards: [], boardId: null,
      sprints: [], selectedSprintId: null,
      issues: [], sprintReport: null, velocityData: null,
    });
    get().fetchBoards(teamId, projectKey);
  },

  selectBoard: (teamId, boardId) => {
    set({ boardId, sprints: [], selectedSprintId: null, issues: [], sprintReport: null, velocityData: null });
    get().fetchSprints(teamId, boardId).then(() => {
      const { sprints, selectSprint, fetchIssues } = useJiraStore.getState();
      const active = sprints.find(s => s.state === 'active') ?? sprints[0] ?? null;
      if (active) {
        selectSprint(teamId, active.id);
      } else {
        fetchIssues(teamId);
      }
    });
  },

  fetchSprints: async (teamId, boardId?) => {
    set({ loadingSprints: true, error: null });
    try {
      const url = boardId
        ? `${API_BASE_URL}/teams/${teamId}/jira/sprints/?board_id=${boardId}`
        : `${API_BASE_URL}/teams/${teamId}/jira/sprints/`;
      const res = await fetch(url, { headers: authHeaders() });
      const data = await handleResponse(res);
      if (data) {
        set({
          sprints: data.sprints || [],
          boardId: data.board_id || boardId || null,
          loadingSprints: false,
        });
      } else {
        set({ sprints: [], loadingSprints: false });
      }
    } catch (e: any) {
      set({ error: e.message, loadingSprints: false });
    }
  },

  fetchIssues: async (teamId, sprintId?) => {
    set({ loadingIssues: true, error: null });
    try {
      const url = sprintId
        ? `${API_BASE_URL}/teams/${teamId}/jira/issues/?sprint_id=${sprintId}`
        : `${API_BASE_URL}/teams/${teamId}/jira/issues/`;
      const res = await fetch(url, { headers: authHeaders() });
      const data = await handleResponse(res);
      set({ issues: data || [], loadingIssues: false });
    } catch (e: any) {
      set({ error: e.message, loadingIssues: false });
    }
  },

  fetchSprintReport: async (teamId, sprintId, sprintName = '') => {
    set({ loadingReport: true, error: null });
    try {
      const params = new URLSearchParams({ sprint_id: String(sprintId), sprint_name: sprintName });
      const res = await fetch(`${API_BASE_URL}/teams/${teamId}/jira/report/?${params}`, { headers: authHeaders() });
      const data = await handleResponse(res);
      set({ sprintReport: data, loadingReport: false });
    } catch (e: any) {
      set({ error: e.message, loadingReport: false });
    }
  },

  fetchVelocity: async (teamId, boardId) => {
    set({ loadingVelocity: true, velocityData: null, error: null });
    try {
      const res = await fetch(
        `${API_BASE_URL}/teams/${teamId}/jira/velocity/?board_id=${boardId}`,
        { headers: authHeaders() },
      );
      const data = await handleResponse(res);
      set({ velocityData: data, loadingVelocity: false });
    } catch (e: any) {
      set({ error: e.message, loadingVelocity: false });
    }
  },

  fetchBurnup: async (teamId, sprintId) => {
    set({ loadingBurnup: true, burnupData: null, error: null });
    try {
      const res = await fetch(
        `${API_BASE_URL}/teams/${teamId}/jira/burnup/?sprint_id=${sprintId}`,
        { headers: authHeaders() },
      );
      const data = await handleResponse(res);
      set({ burnupData: data, loadingBurnup: false });
    } catch (e: any) {
      set({ error: e.message, loadingBurnup: false });
    }
  },

  fetchDeploymentFrequency: async (teamId, sprintId) => {
    set({ loadingDeploymentFrequency: true, deploymentFrequencyData: null, error: null });
    try {
      const res = await fetch(
        `${API_BASE_URL}/teams/${teamId}/jira/deployment-frequency/?sprint_id=${sprintId}`,
        { headers: authHeaders() },
      );
      const data = await handleResponse(res);
      set({ deploymentFrequencyData: data, loadingDeploymentFrequency: false });
    } catch (e: any) {
      set({ error: e.message, loadingDeploymentFrequency: false });
    }
  },

  selectSprint: (teamId, sprintId) => {
    set({ selectedSprintId: sprintId, sprintReport: null });
    get().fetchIssues(teamId, sprintId ?? undefined);
  },

  reset: () => set({
    integration: null,
    projects: [], boards: [], selectedProjectKey: null,
    sprints: [], issues: [], selectedSprintId: null,
    sprintReport: null, boardId: null, velocityData: null,
    burnupData: null, deploymentFrequencyData: null,
    loadingIntegration: false, loadingProjects: false, loadingBoards: false,
    loadingSprints: false, loadingIssues: false, loadingReport: false,
    loadingVelocity: false, loadingBurnup: false, loadingDeploymentFrequency: false,
    error: null,
  }),
}));
